import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UserAutocomplete from '../../../Hooks/useUserAutocomplete';
import { getDeviceTypeOptionsWithLegacy, normalizeDeviceType } from '../../../utils/deviceTypes';

const STATUS_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'expired', label: 'Expired' },
];

const SectionHeader = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      fontWeight: 700,
      color: 'text.secondary',
      letterSpacing: 1.2,
      mt: 1,
      mb: 0.5,
      display: 'block',
    }}
  >
    {children}
  </Typography>
);

const FieldRow = ({ label, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      py: 1,
      px: 1.5,
      borderRadius: 1,
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, minWidth: 140, flexShrink: 0, color: 'text.secondary' }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const EditDeviceDialog = ({
  open,
  onClose,
  device,
  availableRegions,
  isRegionsLoading,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    user: null,
    is_active: true,
    region: null,
    device_type: '',
    is_lost: false,
    is_broken: false,
    device_registration_status: '',
    tailscale_approval_status: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (device && open) {
      const dt = normalizeDeviceType(device.device_type) || device.device_type || '';
      setFormData({
        user: device.user || null,
        is_active: device.is_active ?? true,
        region: device.region || null,
        device_type: dt,
        is_lost: device.is_lost ?? false,
        is_broken: device.is_broken ?? false,
        device_registration_status: device.device_registration_status || '',
        tailscale_approval_status: device.tailscale_approval_status || '',
      });
      setErrors({});
    }
  }, [device, open]);

  const resolvedDeviceType = useMemo(() => normalizeDeviceType(formData.device_type), [formData.device_type]);
  const originalDeviceType = useMemo(() => normalizeDeviceType(device?.device_type) || device?.device_type || '', [device]);
  const deviceTypeOptions = useMemo(() => getDeviceTypeOptionsWithLegacy(formData.device_type), [formData.device_type]);

  const changes = useMemo(() => {
    if (!device) return {};
    const c = {};

    if (formData.user?.id !== device.user?.id) {
      c.user = {
        from: device.user ? (device.user.display_name || device.user.email) : 'None',
        to: formData.user ? (formData.user.display_name || formData.user.email) : 'None',
      };
    }
    if (formData.is_active !== device.is_active) {
      c.is_active = { from: device.is_active ? 'Yes' : 'No', to: formData.is_active ? 'Yes' : 'No' };
    }
    if (formData.region?.id !== device.region?.id) {
      c.region = {
        from: device.region ? device.region.code : 'None',
        to: formData.region ? formData.region.code : 'None',
      };
    }
    if (resolvedDeviceType !== originalDeviceType) {
      c.device_type = { from: originalDeviceType || 'N/A', to: resolvedDeviceType || 'N/A' };
    }
    if (formData.is_lost !== device.is_lost) {
      c.is_lost = { from: device.is_lost ? 'Yes' : 'No', to: formData.is_lost ? 'Yes' : 'No' };
    }
    if (formData.is_broken !== device.is_broken) {
      c.is_broken = { from: device.is_broken ? 'Yes' : 'No', to: formData.is_broken ? 'Yes' : 'No' };
    }
    if ((formData.device_registration_status || '') !== (device.device_registration_status || '')) {
      c.device_registration_status = {
        from: device.device_registration_status || 'N/A',
        to: formData.device_registration_status || 'N/A',
      };
    }
    if ((formData.tailscale_approval_status || '') !== (device.tailscale_approval_status || '')) {
      c.tailscale_approval_status = {
        from: device.tailscale_approval_status || 'N/A',
        to: formData.tailscale_approval_status || 'N/A',
      };
    }
    return c;
  }, [device, formData, resolvedDeviceType, originalDeviceType]);

  const apiPayload = useMemo(() => {
    if (!device) return {};
    const p = {};
    if (formData.user?.id !== device.user?.id) p.user = formData.user?.id || null;
    if (formData.is_active !== device.is_active) p.is_active = formData.is_active;
    if (formData.region?.id !== device.region?.id) p.region = formData.region?.id ?? null;
    if (resolvedDeviceType !== originalDeviceType) p.device_type = resolvedDeviceType || null;
    if (formData.is_lost !== device.is_lost) p.is_lost = formData.is_lost;
    if (formData.is_broken !== device.is_broken) p.is_broken = formData.is_broken;
    if ((formData.device_registration_status || '') !== (device.device_registration_status || ''))
      p.device_registration_status = formData.device_registration_status || null;
    if ((formData.tailscale_approval_status || '') !== (device.tailscale_approval_status || ''))
      p.tailscale_approval_status = formData.tailscale_approval_status || null;
    return p;
  }, [device, formData, resolvedDeviceType, originalDeviceType]);

  const hasChanges = Object.keys(changes).length > 0;
  const hasPayload = Object.keys(apiPayload).length > 0;

  const handleSubmit = () => {
    if (!hasPayload) {
      setErrors({ general: 'No changes to save' });
      return;
    }
    onSubmit(apiPayload);
  };

  const handleClose = () => {
    setFormData({
      user: null,
      is_active: true,
      region: null,
      device_type: '',
      is_lost: false,
      is_broken: false,
      device_registration_status: '',
      tailscale_approval_status: '',
    });
    setErrors({});
    onClose();
  };

  const update = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  if (!device) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Box>
            <Typography variant="h6" component="span">
              Overwrite Device
            </Typography>
            <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ ml: 1 }} component="span">
              {device.mac_addr}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
          All changes are recorded. The device will be updated immediately.
        </Alert>

        {/* --- Assignment --- */}
        <SectionHeader>Assignment</SectionHeader>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <FieldRow label="User">
            <UserAutocomplete
              value={formData.user}
              onChange={(v) => update('user', v)}
              disabled={isLoading}
              size="small"
              label=""
              placeholder="Search user..."
            />
          </FieldRow>
          <FieldRow label="Region">
            <FormControl fullWidth size="small">
              <Select
                value={formData.region?.code || ''}
                onChange={(e) => {
                  const sel = availableRegions.find((r) => r.code === e.target.value);
                  update('region', sel || null);
                }}
                displayEmpty
                disabled={isLoading || isRegionsLoading}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {availableRegions.map((r) => (
                  <MenuItem key={r.id} value={r.code}>
                    {r.code} - {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldRow>
        </Paper>

        {/* --- Hardware --- */}
        <SectionHeader>Hardware</SectionHeader>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <FieldRow label="Device Type">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.device_type}
                  onChange={(e) => update('device_type', e.target.value)}
                  displayEmpty
                  disabled={isLoading}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {deviceTypeOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </FieldRow>
        </Paper>

        {/* --- Status --- */}
        <SectionHeader>Status</SectionHeader>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <FieldRow label="Active">
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => update('is_active', e.target.checked)}
                  color="success"
                  disabled={isLoading}
                />
              }
              label={formData.is_active ? 'Yes' : 'No'}
              sx={{ ml: 0 }}
            />
          </FieldRow>
          <FieldRow label="Registration">
            <FormControl fullWidth size="small">
              <Select
                value={formData.device_registration_status}
                onChange={(e) => update('device_registration_status', e.target.value)}
                displayEmpty
                disabled={isLoading}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldRow>
          <FieldRow label="Tailscale">
            <FormControl fullWidth size="small">
              <Select
                value={formData.tailscale_approval_status}
                onChange={(e) => update('tailscale_approval_status', e.target.value)}
                displayEmpty
                disabled={isLoading}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldRow>
        </Paper>

        {/* --- Flags --- */}
        <SectionHeader>Condition Flags</SectionHeader>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: (formData.is_lost || formData.is_broken) ? 'error.50' : undefined,
            borderColor: (formData.is_lost || formData.is_broken) ? 'error.light' : undefined,
          }}
        >
          <Box sx={{ display: 'flex', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_lost}
                  onChange={(e) => update('is_lost', e.target.checked)}
                  color="error"
                  disabled={isLoading}
                />
              }
              label="Lost"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_broken}
                  onChange={(e) => update('is_broken', e.target.checked)}
                  color="error"
                  disabled={isLoading}
                />
              }
              label="Broken"
            />
          </Box>
        </Paper>

        {/* --- Changes Preview --- */}
        {hasChanges && (
          <>
            <Divider sx={{ my: 1 }} />
            <SectionHeader>Changes Preview</SectionHeader>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {Object.entries(changes).map(([key, { from, to }]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 120, color: 'text.secondary' }}>
                    {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Typography>
                  <Chip label={from} size="small" variant="outlined" sx={{ maxWidth: 150 }} />
                  <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Chip label={to} size="small" color="primary" sx={{ maxWidth: 150 }} />
                </Box>
              ))}
            </Box>
          </>
        )}

        {errors.general && (
          <Alert severity="error" sx={{ mt: 2 }}>{errors.general}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={isLoading || !hasPayload}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : `Overwrite${hasChanges ? ` (${Object.keys(changes).length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDeviceDialog;
